// Staff de jueces del torneo.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  UserWithToken,
} from '../api.types';

// Tournament Judge Staff

const TOURNAMENT_JUDGE_FIELDS = `
  id
  userId
  tournamentId
  position
  description
  status
  user {
    id
    name
    secondName
    lastName
    secondLastName
    cossyId
    picture
  }
  judge {
    id
    rc
    pc
    dc
  }
  createdAt
  updatedAt
`;

export const assignTournamentJudge = async (
  user: UserWithToken,
  tournamentId: number,
  userId: number,
  position: string,
  description?: string | null
) =>
  getGraphQLQueryClient(user).request<{
    data: import('@ygoargentina/sdk/api/api.types').TournamentJudge;
  }>(
    gql`
      mutation assignTournamentJudge(
        $tournamentId: Int!
        $input: AssignTournamentJudgeInput!
      ) {
        data: assignTournamentJudge(tournamentId: $tournamentId, input: $input) {
          ${TOURNAMENT_JUDGE_FIELDS}
        }
      }
    `,
    {
      tournamentId,
      input: { userId, position, description: description ?? null },
    }
  );

export const updateTournamentJudgePosition = async (
  user: UserWithToken,
  tournamentId: number,
  userId: number,
  position: string,
  description?: string | null
) =>
  getGraphQLQueryClient(user).request<{
    data: import('@ygoargentina/sdk/api/api.types').TournamentJudge;
  }>(
    gql`
      mutation updateTournamentJudgePosition(
        $tournamentId: Int!
        $userId: Int!
        $position: JudgePosition!
        $description: String
      ) {
        data: updateTournamentJudgePosition(
          tournamentId: $tournamentId
          userId: $userId
          position: $position
          description: $description
        ) {
          ${TOURNAMENT_JUDGE_FIELDS}
        }
      }
    `,
    {
      tournamentId,
      userId,
      position,
      description: description ?? null,
    }
  );

export const removeTournamentJudge = async (
  user: UserWithToken,
  tournamentId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<{ data: boolean }>(
    gql`
      mutation removeTournamentJudge($tournamentId: Int!, $userId: Int!) {
        data: removeTournamentJudge(
          tournamentId: $tournamentId
          userId: $userId
        )
      }
    `,
    {
      tournamentId,
      userId,
    }
  );

export const inviteJudgeToTournament = async (
  user: UserWithToken,
  tournamentId: number,
  userId: number,
  position: string,
  description?: string | null
) =>
  getGraphQLQueryClient(user).request<{
    data: import('@ygoargentina/sdk/api/api.types').TournamentJudge;
  }>(
    gql`
      mutation inviteJudgeToTournament(
        $tournamentId: Int!
        $input: InviteTournamentJudgeInput!
      ) {
        data: inviteJudgeToTournament(tournamentId: $tournamentId, input: $input) {
          ${TOURNAMENT_JUDGE_FIELDS}
        }
      }
    `,
    {
      tournamentId,
      input: { userId, position, description: description ?? null },
    }
  );

export const respondJudgeInvitation = async (
  user: UserWithToken,
  tournamentId: number,
  accept: boolean
) =>
  getGraphQLQueryClient(user).request<{
    data: import('@ygoargentina/sdk/api/api.types').TournamentJudge;
  }>(
    gql`
      mutation respondJudgeInvitation($tournamentId: Int!, $accept: Boolean!) {
        data: respondJudgeInvitation(tournamentId: $tournamentId, accept: $accept) {
          ${TOURNAMENT_JUDGE_FIELDS}
        }
      }
    `,
    {
      tournamentId,
      accept,
    }
  );

export const getTournamentJudges = async (
  user: UserWithToken | null,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: import('@ygoargentina/sdk/api/api.types').TournamentJudge[];
  }>(
    gql`
      query tournamentJudges($tournamentId: Int!) {
        data: tournamentJudges(tournamentId: $tournamentId) {
          ${TOURNAMENT_JUDGE_FIELDS}
        }
      }
    `,
    { tournamentId }
  );

export const getMyJudgeTournaments = async (
  user: UserWithToken,
  status?: 'INVITED' | 'ACCEPTED' | 'DECLINED'
) =>
  getGraphQLQueryClient(user).request<{
    data: Array<
      import('@ygoargentina/sdk/api/api.types').TournamentJudge & {
        tournament: import('@ygoargentina/sdk/api/api.types').Tournament;
      }
    >;
  }>(
    gql`
      query myJudgeTournaments($status: TournamentJudgeStatus) {
        data: myJudgeTournaments(status: $status) {
          ${TOURNAMENT_JUDGE_FIELDS}
          tournament {
            id
            title
            dateFrom
            dateTo
            status
          }
        }
      }
    `,
    { status: status ?? null }
  );
